import { useState } from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { OfflineInfoDialog } from './OfflineInfoDialog';

interface OfflineStatusIndicatorProps {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  onSync: () => void;
}

export function OfflineStatusIndicator({
  isOnline,
  isSyncing,
  pendingCount,
  onSync,
}: OfflineStatusIndicatorProps) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <TooltipProvider>
      <div className="flex items-center gap-2">
        {/* Help button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowInfo(true)}
            >
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Como funciona o modo offline</p>
          </TooltipContent>
        </Tooltip>

        {/* Pending orders badge */}
        {pendingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge 
                variant="secondary" 
                className={cn(
                  "gap-1 cursor-pointer",
                  !isOnline && "bg-warning/20 text-warning-foreground border-warning"
                )}
                onClick={onSync}
              >
                {isOnline ? (
                  <Cloud className="h-3 w-3" />
                ) : (
                  <CloudOff className="h-3 w-3" />
                )}
                {pendingCount} pendente{pendingCount > 1 ? 's' : ''}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isOnline
                  ? 'Clique para sincronizar agora'
                  : 'Pedidos salvos localmente. Serão sincronizados quando online.'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Sync button */}
        {pendingCount > 0 && isOnline && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onSync}
                disabled={isSyncing}
              >
                <RefreshCw
                  className={cn('h-4 w-4', isSyncing && 'animate-spin')}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Sincronizar pedidos</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Connection status */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
                isOnline
                  ? 'bg-success/10 text-success'
                  : 'bg-destructive/10 text-destructive'
              )}
            >
              {isOnline ? (
                <>
                  <Wifi className="h-3 w-3" />
                  <span className="hidden sm:inline">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  <span className="hidden sm:inline">Offline</span>
                </>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isOnline
                ? 'Conectado à internet'
                : 'Sem conexão. Modo offline ativo.'}
            </p>
          </TooltipContent>
        </Tooltip>

        {/* Info Dialog */}
        <OfflineInfoDialog open={showInfo} onOpenChange={setShowInfo} />
      </div>
    </TooltipProvider>
  );
}
